import { useAuth0 } from "@auth0/auth0-react";
import * as E from "fp-ts/Either";
import * as TE from "fp-ts/TaskEither";
import { pipe } from "fp-ts/function";
import { useEffect, useState } from "react";
import * as OE from "./option-either";
import { stateFromAsyncEffectK } from "./state-from";

type UseAuth0Result = ReturnType<typeof useAuth0>;
type getAccessTokenSilently = UseAuth0Result["getAccessTokenSilently"];
type getAccessTokenSilentlyOptions = Parameters<
  UseAuth0Result["getAccessTokenSilently"]
>[0];

const getAccessTokenSilentlyWithOptions = (
  getAccessTokenSilently: getAccessTokenSilently,
  getAccessTokenSilentlyOptions?: getAccessTokenSilentlyOptions,
) =>
  pipe(
    TE.tryCatch(
      () => getAccessTokenSilently(getAccessTokenSilentlyOptions),
      E.toError,
    ),
    // defensively match against the (IMO slightly suspect) overloaded typings of `getAccessTokenSilently`
    // to match the previous type - just a string
    TE.map((x: Awaited<ReturnType<getAccessTokenSilently>>) =>
      typeof x === "string" ? x : x.access_token,
    ),
  );

export const useAccessToken = (
  getAccessTokenSilentlyOptions?: getAccessTokenSilentlyOptions,
) => {
  const { getAccessTokenSilently } = useAuth0();
  return stateFromAsyncEffectK(getAccessTokenSilentlyWithOptions)(
    getAccessTokenSilently,
    getAccessTokenSilentlyOptions,
  );
};
export type UseAccessTokenResult = ReturnType<typeof useAccessToken>;
export type UseAccessTokenError = OE.ExtractLeft<UseAccessTokenResult>;
export type UseAccessTokenSuccess = OE.ExtractRight<UseAccessTokenResult>;

export const useAccessTokenBasic = (
  getAccessTokenSilentlyOptions?: getAccessTokenSilentlyOptions,
) => {
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const getAccessToken = async () => {
      setAccessToken(
        await getAccessTokenSilently(getAccessTokenSilentlyOptions),
      );
    };

    void getAccessToken();
  }, [getAccessTokenSilently, getAccessTokenSilentlyOptions]);

  return accessToken;
};

export const useAccessTokenStruct = () => pipe(useAccessToken(), OE.toStruct);
